import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnInit,
    output,
    signal,
    ViewEncapsulation
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import {
    catchError,
    filter,
    finalize,
    map,
    merge,
    Observable,
    ReplaySubject,
    scan,
    Subject,
    switchMap,
    tap
} from "rxjs";
import { AnvandareDTO } from "~app/common/models/anvandare/AnvandareDTO";
import { AttesteringStatus, Betalningsmetod, KontaktDTO } from "~app/common/models/kontakter/KontaktDTO";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { ApiClientService } from "~app/common/services/api-client.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { BetalningsinformationMediatorService } from "../../betalningsinformation-mediator.service";
import { LeverantorService } from "../../leverantor.service";

type BetalningsinfoFormGroup = FormGroup<{
    betalningsmetod: FormControl<Betalningsmetod>;
    bankgiro: FormControl<string>;
    postgiro: FormControl<string>;
    bankkonto: FormControl<string>;
    clearingnummer: FormControl<string>;
    gln: FormControl<string>;
    iban: FormControl<string>;
    bicSwift: FormControl<string>;
    callbackNotering: FormControl<string>;
    attesterasAv: FormControl<AnvandareDTO>;
    attesteringStatus: FormControl<AttesteringStatus>;
    id: FormControl<number>;
    isCreator: FormControl<boolean>;
}>;

type BetalningsinformationDto = Pick<KontaktDTO,
    "betalningsmetod" |
    "bankgiro" |
    "postgiro" |
    "bankkonto" |
    "clearingnummer" |
    "gln" |
    "iban" |
    "bicSwift" |
    "callbackNotering" |
    "attesterasAv" |
    "attesteringStatus"
> & {
    isCreator?: boolean;
};

type BetalningsinformationReducer = (current: BetalningsinformationDto) => BetalningsinformationDto;

export type BetalningsinformationSearchParams = Pick<BetalningsinformationDto, "attesterasAv">;

export type BetalningsinformationChangeEvent = {
    betalningsinformation: BetalningsinformationDto;
};

export const BetalningsinformationSearchParamsKey = "betalningsinformation.searchParams";

@Component({
    selector: "rask-betalningsinformation",
    imports: [
        RaskCommonModule,
        TranslatePipe
    ],
    templateUrl: "./betalningsinformation.component.html",
    styleUrl: "./betalningsinformation.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class BetalningsinformationComponent implements OnInit {
    @Input()
    set kontakt(value: KontaktDTO) {
        this.kontaktSrc.next(value);
    }
    readonly betalningsinformationChange = output<BetalningsinformationChangeEvent>();
    protected form: BetalningsinfoFormGroup;
    protected betalningsmetodValues: {
        id: string;
        namn: string;
    }[];

    protected readonly sendClickSrc = new Subject<void>();
    protected readonly approveClickSrc = new Subject<void>();
    protected readonly rejectClickSrc = new Subject<void>();
    protected readonly deleteClickSrc = new Subject<void>();
    protected betalningsinformation$: Observable<BetalningsinformationDto>;
    protected utkast$: Observable<BetalningsinformationDto>;
    protected readonly isLoading = signal<boolean>(false);
    private readonly fb = inject(FormBuilder);
    private readonly apiClientService = inject(ApiClientService);
    private readonly leverantorService = inject(LeverantorService);
    private readonly webinstallningarService = inject(WebinstallningarService);
    private readonly activeModal = inject(NgbActiveModal);
    private readonly betalningsinformationMediatorService = inject(BetalningsinformationMediatorService);
    private readonly kontaktSrc = new ReplaySubject<KontaktDTO>(1);
    constructor() {
        this.form = this.fb.group({
            betalningsmetod: [
null as Betalningsmetod,
[Validators.required]
            ],
            bankgiro: [
                { value: "",
                    disabled: true },
                [Validators.required]
            ],
            postgiro: [
                { value: "",
                    disabled: true },
                [Validators.required]
            ],
            bankkonto: [
                { value: "",
                    disabled: true },
                [Validators.required]
            ],
            clearingnummer: [
                { value: "",
                    disabled: true },
                [Validators.required]
            ],
            bicSwift: [""],
            gln: [""],
            iban: [""],
            callbackNotering: [
                "",
                [Validators.minLength(10)]
            ],
            attesterasAv: [
null as AnvandareDTO,
[Validators.required]
            ],
            attesteringStatus: [AttesteringStatus.Skapad],
            id: [0],
            isCreator: [true]
        });
    }

    ngOnInit(): void {
        this.betalningsmetodValues = this.leverantorService.getBetalningsmetoder();

        const kontaktReducer: Observable<BetalningsinformationReducer> = this.kontaktSrc.pipe(
            map((k) => (_current: BetalningsinformationDto) => ({
                bankgiro: k.bankgiro,
                bankkonto: k.bankkonto,
                betalningsmetod: k.betalningsmetod,
                bicSwift: k.bicSwift,
                clearingnummer: k.clearingnummer,
                attesteringStatus: k.attesteringStatus,
                attesterasAv: k.attesterasAv,
                callbackNotering: k.callbackNotering || this.getInitialCallbackNotering(k.betalningsmetod),
                gln: k.gln,
                iban: k.iban,
                postgiro: k.postgiro
            }))
        );

        const searchParams = this.webinstallningarService.get<BetalningsinformationSearchParams>(BetalningsinformationSearchParamsKey);

        this.form.patchValue({
            ...searchParams
        }, { emitEvent: false });

        const saveReducer: Observable<BetalningsinformationReducer> = this.betalningsinformationMediatorService.save$.pipe(
            tap(() => {
                const { attesterasAv } = this.form.value;
                if (attesterasAv) {
                    this.webinstallningarService.set<BetalningsinformationSearchParams>(BetalningsinformationSearchParamsKey, {
                        attesterasAv: attesterasAv
                    });
                }
                this.form.markAsPristine();
            }),
            map(() => (current: BetalningsinformationDto) => current)
        );

        const sendReducer: Observable<BetalningsinformationReducer> = this.sendClickSrc.pipe(
            filter(() => this.form.valid),
            tap(() => this.isLoading.set(true)),
            switchMap(() => this.sendBetalningsinformationUtkast(this.form.controls.id.value)),
            tap(() => this.form.patchValue({
                attesteringStatus: AttesteringStatus.Skickad
            }, { emitEvent: false })),
            map(() => (current: BetalningsinformationDto) => current)
        );

        const betalningsmetodReducer: Observable<BetalningsinformationReducer> = this.form.controls.betalningsmetod.valueChanges.pipe(
            tap(val => this.handleBetalningsmetodChange(val)),
            map(() => (current: BetalningsinformationDto) => current)
        );

        const approveReducer: Observable<BetalningsinformationReducer> = this.approveClickSrc.pipe(
            tap(() => this.isLoading.set(true)),
            switchMap(() => this.approveBetalningsinformationUtkast(this.form.controls.id.value)),
            tap(() => this.form.patchValue({
                attesteringStatus: AttesteringStatus.Godkand
            }, { emitEvent: false })),
            map(() => (current: BetalningsinformationDto) => current)
        );

        const rejectReducer: Observable<BetalningsinformationReducer> = this.rejectClickSrc.pipe(
            tap(() => this.isLoading.set(true)),
            switchMap(() => this.rejectBetalningsinformationUtkast(this.form.controls.id.value)),
            tap(() => this.form.patchValue({
                attesteringStatus: AttesteringStatus.Avvisad
            }, { emitEvent: false })),
            map(() => (current: BetalningsinformationDto) => current)
        );

        const deleteReducer: Observable<BetalningsinformationReducer> = this.deleteClickSrc.pipe(
            tap(() => this.isLoading.set(true)),
            switchMap(() => this.deleteBetalningsinformationUtkast(this.form.controls.id.value)),
            tap(() => this.form.reset()),
            map(() => (current: BetalningsinformationDto) => current)
        );

        const fetchUtkastReducer: Observable<BetalningsinformationReducer> = this.kontaktSrc.pipe(
            switchMap(k => this.getBetalningsinformationUtkast(k.id)),
            filter(b => ObjectUtils.hasValue(b)),
            tap(b => this.updateFormValues(b)),
            map(() => (current: BetalningsinformationDto) => current)
        );

        const valueChangeReducer: Observable<BetalningsinformationReducer> = this.form.valueChanges.pipe(
            map((values) => (current: BetalningsinformationDto) => {
                this.betalningsinformationChange.emit({
                    betalningsinformation: {
                        ...values
                    }
                });
                return current;
            })
        );

        const actionReducer: Observable<BetalningsinformationReducer> = merge(
            sendReducer,
            approveReducer,
            rejectReducer,
            deleteReducer
        ).pipe(
            finalize(() => this.isLoading.set(false)),
            tap(() => this.activeModal.close()),
            catchError((_e, c) => c)
        );

        this.betalningsinformation$ = merge(
            kontaktReducer,
            fetchUtkastReducer,
            valueChangeReducer,
            actionReducer,
            betalningsmetodReducer,
            saveReducer
        ).pipe(
            scan((acc: BetalningsinformationDto, reducer: BetalningsinformationReducer) => reducer(acc), {})
        );
    }

    protected getBetalningsmetodName(key: string) {
        return this.betalningsmetodValues.find(x => x.id === key)?.namn;
    }

    protected get isSkapad() {
        return this.form.controls.attesteringStatus.value === AttesteringStatus.Skapad;
    }

    protected getInitialCallbackNotering(betalningsmetod: Betalningsmetod): string {
        switch (betalningsmetod) {
            case "Konto":
                return "Ringde till <namn> <datum> med <nummer> (som hämtades från kontakten)";
            case "Bankgiro":
            case "Postgiro":
                return "Verifierat bankgiro/plusgiro via bankgirot.se <datum>";
        }
        return "";
    }

    protected get noteringPlaceholder() {
        return this.getInitialCallbackNotering(this.form.controls.betalningsmetod.value);
    }

    protected searchForAnsvarigPlanerareTypeAhead =
        (input: string) => this.apiClientService.get<ITypeAheadResult[]>(`typeahead/anvandare/kopledare?input=${input}`).pipe(
            map(result => result.map<AnvandareDTO>(x => ({
                namn: x.namn,
                userName: x.kod,
                id: x.id,
                efternamn: "",
                fornamn: "",
                rowVersion: "",
                userId: ""
            })))
        );

    protected get canEdit() {
        const { attesteringStatus, isCreator } = this.form.value;

        const editableStatuses = [
            AttesteringStatus.Skapad,
            AttesteringStatus.Avvisad
        ];
        return editableStatuses.includes(attesteringStatus) && isCreator;
    }

    private handleBetalningsmetodChange(betalningsmetod: Betalningsmetod) {
        this.form.enable({ emitEvent: false });
        const newCallbackText = this.getInitialCallbackNotering(betalningsmetod);
        this.form.controls.callbackNotering.setValue(newCallbackText, { emitEvent: false });
        this.enableDisableBetalningsmetodFields(betalningsmetod);
    }

    private enableDisableBetalningsmetodFields(betalningsmetod: Betalningsmetod) {
        switch (betalningsmetod) {
            case Betalningsmetod.Bankkonto: {
                this.form.controls.postgiro.disable({ emitEvent: false });
                this.form.controls.bankgiro.disable({ emitEvent: false });
                break;
            }
            case Betalningsmetod.Bankgiro: {
                this.form.controls.bankkonto.disable({ emitEvent: false });
                this.form.controls.clearingnummer.disable({ emitEvent: false });
                this.form.controls.postgiro.disable({ emitEvent: false });
                break;
            }
            case Betalningsmetod.Postgiro: {
                this.form.controls.bankkonto.disable({ emitEvent: false });
                this.form.controls.clearingnummer.disable({ emitEvent: false });
                this.form.controls.bankgiro.disable({ emitEvent: false });
                break;
            }
            default: {
                this.form.controls.bankkonto.disable({ emitEvent: false });
                this.form.controls.clearingnummer.disable({ emitEvent: false });
                this.form.controls.postgiro.disable({ emitEvent: false });
                this.form.controls.bankgiro.disable({ emitEvent: false });
            }
        }
    }

    private getBetalningsinformationUtkast(virkesleverantorId: number) {
        return this.apiClientService.get<BetalningsinformationDto>(`betalningsinformation/${virkesleverantorId}`);
    }

    private sendBetalningsinformationUtkast(betalningsinformationUtkastId: number) {
        return this.apiClientService.post(`betalningsinformation/${betalningsinformationUtkastId}/send`, null);
    }

    private approveBetalningsinformationUtkast(betalningsinformationUtkastId: number) {
        return this.apiClientService.post(`betalningsinformation/${betalningsinformationUtkastId}/approve`, null);
    }

    private rejectBetalningsinformationUtkast(betalningsinformationUtkastId: number) {
        return this.apiClientService.post(`betalningsinformation/${betalningsinformationUtkastId}/reject`, null);
    }

    private deleteBetalningsinformationUtkast(betalningsinformationUtkastId: number) {
        return this.apiClientService.delete(`betalningsinformation/${betalningsinformationUtkastId}`, null);
    }

    private updateFormValues(values: BetalningsinformationDto) {
        if (!values) {
            return;
        }
        this.form.patchValue({
            ...values
        }, { emitEvent: false });

        if (this.canEdit) {
            this.form.enable({ emitEvent: false });
            this.enableDisableBetalningsmetodFields(values.betalningsmetod);
        } else {
            this.form.disable({ emitEvent: false });
        }
    }
}
